/**
 * IPFS Client using Filebase (NO INFURA)
 *
 * Upload: Uses server-side API endpoint to upload to Filebase S3/IPFS
 * Retrieve: Uses multi-gateway IPFSService for reliable content fetching
 */

import ipfsService from '../services/IPFSService';
import filebaseService from '../services/filebaseService';

// Function to upload JSON data to IPFS via Filebase
export const uploadJsonToIPFS = async (jsonData) => {
  try {
    console.log('Starting JSON upload to IPFS via Filebase...');

    // Convert JSON to Blob
    const jsonString = JSON.stringify(jsonData);
    const blob = new Blob([jsonString], { type: 'application/json' });

    // Create a File from the Blob with a filename
    const timestamp = Date.now();
    const file = new File([blob], `${timestamp}.json`, { type: 'application/json' });

    // Upload via Filebase service
    const result = await filebaseService.uploadFile(file, {
      description: 'JSON data upload',
      dataType: 'json'
    });

    console.log('IPFS upload result:', result);

    // Return in the same format as before for compatibility
    return {
      path: result.cid,
      url: result.gatewayUrl,
      size: result.fileSize,
      fileName: result.fileName,
      ipfsGatewayUrl: result.gatewayUrl,
      cid: result.cid
    };
  } catch (error) {
    console.error('Error uploading JSON to IPFS:', error);
    throw error;
  }
};

// Function to upload a file to IPFS via Filebase
export const uploadFileToIPFS = async (file) => {
  try {
    console.log('Starting file upload to IPFS via Filebase...', file.name, file.type);

    // Upload via Filebase service
    const result = await filebaseService.uploadFile(file, {
      description: `File upload: ${file.name}`,
      originalName: file.name
    });

    console.log('IPFS upload result:', result);

    // Return in the same format as before for compatibility
    return {
      path: result.cid,
      url: result.gatewayUrl,
      size: result.fileSize,
      fileName: result.originalName,
      fileType: result.contentType,
      ipfsGatewayUrl: result.gatewayUrl,
      cid: result.cid
    };
  } catch (error) {
    console.error('Error uploading file to IPFS:', error);
    throw error;
  }
};

// Function to get JSON from IPFS by its CID using multi-gateway IPFSService
export const getJsonFromIPFS = async (cid) => {
  try {
    console.log('Getting JSON from IPFS:', cid);

    // Try to fetch and parse as JSON first
    try {
      const jsonData = await ipfsService.fetchJSON(cid);
      const url = ipfsService.getPrimaryGatewayUrl(cid);
      return { url, data: jsonData };
    } catch (jsonError) {
      // If JSON parsing fails, try to get as text (might be encrypted)
      console.log('JSON parsing failed, trying as text...');
      const textContent = await ipfsService.fetchText(cid);
      const url = ipfsService.getPrimaryGatewayUrl(cid);
      return { url, data: textContent };
    }
  } catch (error) {
    console.error('Error getting JSON from IPFS:', error);
    throw error;
  }
};

// Export services for direct use if needed
export { ipfsService, filebaseService };

// Export default for backward compatibility (though not using ipfs-http-client anymore)
const ipfsClient = { uploadJsonToIPFS, uploadFileToIPFS, getJsonFromIPFS };
export default ipfsClient;